import { useCurrentUser } from '@centecomm/portal-service-react';
import { useEffect, useState } from 'react';
import useSettingsFromState from 'State/Settings/Hooks/useSettingsFromState';

export type UseCheckIfCurrentCompanyMatchesIdResult = {
    idMatch: boolean;
    isCurrentCompanyDetermined: boolean;
};

/**
 * This hook checks if the current company (loaded from settings) matches the `targetId` provided.
 * @param targetId The company ID to compare with the current company ID.
 * @returns An object with the result of the comparison and a flag indicating if the current company has been determined.
 */
const useCheckIfCurrentCompanyMatchesId = (targetId: number): UseCheckIfCurrentCompanyMatchesIdResult => {
    const { currentUser } = useCurrentUser();
    const { isLoaded: settingsLoaded, settings } = useSettingsFromState();
    const companyId = settings?.companyId;
    const [isCurrentCompanyDetermined, setIsCurrentCompanyDetermined] = useState(false);
    const [idMatch, setIdMatch] = useState(false);

    useEffect(() => {
        if (isCurrentCompanyDetermined || !settingsLoaded) return;

        // retrieve possible companyIds from the current user and dedupe them
        const usercompanyIds = Array.from(
            new Set(currentUser?.tenant?.occupants?.map((occupant) => occupant.companyId) || []),
        );

        if (companyId === targetId || (usercompanyIds.length === 1 && usercompanyIds[0] === targetId)) {
            setIdMatch(true);
        }

        setIsCurrentCompanyDetermined(true);
    }, [settingsLoaded, companyId]);

    return {
        idMatch,
        isCurrentCompanyDetermined,
    };
};

export default useCheckIfCurrentCompanyMatchesId;
